import {
  Box,
  Divider,
  Flex,
  Link,
  Price,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  Text,
} from "flicket-ui";
import { useAtom } from "jotai";
import flatten from "lodash/flatten";
import isEmpty from "lodash/isEmpty";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";
import { FC, ReactNode, useContext, useEffect, useState } from "react";
import useSWR from "swr";
import { ContentModal } from "~components";
import { Icon, SlateRenderer } from "~components/common";
import { getAfterPayLocaleByCurrency } from "~config";
import { BaseContext } from "~context";
import {
  BNPLIntegration,
  isBNPLIntegrationType,
} from "~features/checkout/checkout.types";
import useActiveReferralCampaign from "~features/referrals/useActiveReferralCampaign";
import BuyNowPayLater from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import { Release } from "~graphql/fetchers";
import {
  EventQuery,
  IntegrationType,
  LandingPage,
  MembershipQuery,
  ReferralRewardType,
  ReleaseType,
  TicketTypeKind,
} from "~graphql/sdk";
import { MembershipsSoldForUserDocument } from "~graphql/typed-document-nodes";
import {
  CreateOrderFn,
  useAccount,
  useIsMobile,
  useNonSeatedMultibuy,
  useOrganization,
  useReferral,
} from "~hooks";
import {
  googleAnalyticsMembershipAtom,
  googleAnalyticsReleaseAtom,
  trackAddToCartV4,
  trackRemoveFromCartV4,
  trackSelectItemV4,
  trackViewItemListV4,
} from "~hooks/useGoogleAnalytics";
import { useQuery } from "~hooks/useQuery";
import {
  calculateMultibuyDiscount,
  calculateTotal,
  OrganizationFeatures,
  sdk,
  showToast,
  trackAddToCart,
  trackRemoveFromCart,
  trackSelectItem,
  trackViewItemList,
} from "~lib";
import { isAvailableForSelection, isVisible } from "./non-seated.helpers";
import { ListItem } from "./non-seated.ListItem";

const hasImportantNotice = (importantNotice?: string) =>
  importantNotice &&
  (JSON.parse(importantNotice) as any[]).some(({ children }) =>
    children.some(({ text }) => !isEmpty(text))
  );

type Addons =
  | MembershipQuery["membership"]["addons"]
  | Release["releaseEventAddons"][number]["eventAddon"][];

type NonSeatedProps = {
  eventOrMembership: EventQuery["event"] | MembershipQuery["membership"];
  zones: any[];
  createOrder: CreateOrderFn;
  isMembership: boolean;
  releaseType?: ReleaseType;
  releaseNotes?: string;
  isDigital?: boolean;
  isResaleRelease?: boolean;
  allowPublicComp?: boolean;
  addons?: Addons;
};

export const List: FC<NonSeatedProps> = ({
  addons: availableAddons,
  eventOrMembership,
  zones,
  createOrder,
  isMembership,
  releaseNotes,
  isDigital,
  isResaleRelease,
  releaseType,
  allowPublicComp,
}) => {
  const { activeReferralCampaign } = useActiveReferralCampaign(
    eventOrMembership.id
  );
  const router = useRouter();
  const { isAdmin, isPOS, isLoggedIn, user } = useAccount();
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [delayedIsSubmitting, setDelayedSubmitting] = useState(false);
  const { organization, isSinglePage } = useContext(BaseContext);
  const { hasFeature } = useOrganization();
  const [importantNotice, setImportantNotice] = useState(releaseNotes);
  const [alertError, setAlertError] = useState<
    { title: string; message: string | ReactNode } | undefined
  >();
  const ticketVariant = isDigital ? "pass code" : "ticket";
  const isMobile = useIsMobile();
  const { calculateReferralDiscount } = useReferral();

  const seats = selectedItems.filter((i) => i.type !== "addon");
  const addons = selectedItems.filter((i) => i.type === "addon");
  const userCredits = user?.credits ?? 0;

  const bnplGateways = eventOrMembership?.gateways?.filter(
    (gateway) => !!gateway && isBNPLIntegrationType(gateway.type)
  );

  const stripeAfterpayGateway = eventOrMembership?.gateways?.find(
    (gateway) =>
      gateway.type === IntegrationType.PaymentStripe && gateway.bnplEnabled
  );

  const showHiddenFees = !!organization.features?.includes(
    OrganizationFeatures.ShowHiddenFees
  );

  useEffect(() => {
    let timer: NodeJS.Timeout = undefined;
    if (isSubmitting) {
      timer = setTimeout(() => {
        setDelayedSubmitting(true);
      }, 2000);
    } else {
      setDelayedSubmitting(false);
    }
    return () => timer && clearTimeout(timer);
  }, [isSubmitting]);

  useEffect(() => {
    releaseNotes
      ? setImportantNotice(releaseNotes)
      : setImportantNotice(eventOrMembership?.importantNotice);
  }, [releaseNotes]);

  const [totalTickets, setTotalTickets] = useState(0);

  const [release] = useAtom(googleAnalyticsReleaseAtom);
  const [membership] = useAtom(googleAnalyticsMembershipAtom);

  useEffect(() => {
    const reducer = (accumulator, currentValue) => accumulator + currentValue;

    const quantityArr = seats.map((seat) => {
      return seat.quantity;
    });

    quantityArr.length > 0
      ? setTotalTickets(quantityArr.reduce(reducer))
      : setTotalTickets(0);
  }, [seats]);

  const { data: ticketsSoldForUserData } = useSWR(
    isLoggedIn && eventOrMembership?.id && !isMembership
      ? ["tickets-sold-for-user", organization.id, eventOrMembership?.id]
      : null,
    async (_: string, orgId: string, eventId: string) =>
      sdk({ orgId })
        .ticketsSoldForUser({ eventId })
        .then(({ ticketsSoldForUser }) => ticketsSoldForUser)
  );

  const { data: membershipsSoldForUserData } = useQuery(
    isLoggedIn &&
      eventOrMembership?.id &&
      isMembership &&
      MembershipsSoldForUserDocument,
    {
      membershipId: eventOrMembership?.id,
    }
  );

  const membershipsSoldForUser =
    membershipsSoldForUserData?.membershipsSoldForUser;

  const ticketsSoldForUser =
    ticketsSoldForUserData ?? membershipsSoldForUser ?? [];

  /** @note because GA multibuy and non GA multibuy differ too much we have a new hook */
  const { activePromotions } = useNonSeatedMultibuy(
    eventOrMembership?.multiBuyPromotions,
    seats.map((s, key) => ({
      ...s,
      id: `${s.id}-${key}`,
      [isMembership ? "membershipType" : "ticketType"]: { id: s.id },
    }))
  );

  const tickets = isMembership
    ? (eventOrMembership as MembershipQuery["membership"])?.types
    : (eventOrMembership as EventQuery["event"])?.ticketTypes;

  const allTypes = isMembership
    ? flatten(zones?.map(({ membershipTypes }) => membershipTypes))
    : flatten(zones?.map(({ ticketTypes }) => ticketTypes));

  const ticketTypes = [];
  for (const ticketType of tickets) {
    const zoneTicketType = allTypes?.find(
      (zone) =>
        zone?.ticketTypeId === ticketType?.id ||
        (zone?.membershipTypeId === ticketType?.id && zone?.isActive)
    );

    if (!zoneTicketType) {
      continue;
    }

    if (
      ticketType.kind !== TicketTypeKind.Standard &&
      !(isAdmin || allowPublicComp)
    ) {
      continue;
    }

    ticketTypes.push({
      ...ticketType,
      zoneTicketType: zoneTicketType,
      purchaseRestrictions: zoneTicketType.purchaseRestrictions,
      purchaseRestrictionsHelpText: zoneTicketType.purchaseRestrictionsHelpText,
    });
  }

  const addonTypes = isAdmin
    ? availableAddons ?? []
    : (availableAddons ?? new Array<Addons[number]>()).filter(
        (addon) => addon.isActive
      );

  const multibuyDiscount = seats?.every(
    (seat) => seat.zoneId === seats?.[0]?.zoneId
  )
    ? calculateMultibuyDiscount(
        activePromotions[0],
        [...addonTypes, ...ticketTypes],
        zones?.find((zone) => zone.name === seats?.[0]?.zoneId)?.ticketTypes ||
          zones?.find((zone) => zone.name === seats?.[0]?.zoneId)
            ?.membershipTypes
      )
    : 0;

  const onChange = (
    id: string,
    value: number,
    type: "event" | "membership" | "addon" = "event",
    zoneId: string,
    price: number
  ) => {
    const current =
      ticketTypes?.find((ticketType) => ticketType.id === id) ||
      addonTypes?.find((addon) => addon.id === id);

    const itemExists = selectedItems.find(
      (item) => item.id === id && item.zoneId === zoneId
    );

    let updatedItem = {
      ...itemExists,
      quantity: value,
      zoneId,
    };

    //if the item doesnt exist then add the details from current to it
    if (!itemExists) {
      updatedItem = {
        ...updatedItem,
        name: current.name,
        id: current.id,
      };
    }

    const trackItem = {
      ...updatedItem,
      price,
    };

    trackSelectItem(trackItem);
    trackSelectItemV4({
      item: trackItem,
      eventOrMembershipId: eventOrMembership.id,
      membership,
      release,
    });

    let itemsToAdd = [];

    if (selectedItems.length === 0) {
      trackAddToCart(trackItem);
      trackAddToCartV4(organization, {
        eventOrMembershipId: eventOrMembership.id,
        item: trackItem,
        membership,
        release,
      });
      itemsToAdd = [{ ...current, quantity: value, zoneId, type }];
    } else if (!itemExists) {
      trackAddToCart(trackItem);
      trackAddToCartV4(organization, {
        eventOrMembershipId: eventOrMembership.id,
        item: trackItem,
        membership,
        release,
      });
      itemsToAdd = [
        ...selectedItems,
        { ...current, quantity: value, zoneId, type },
      ];
    } else {
      const existingSelectedItem = selectedItems?.filter(
        (item) => item.id === id && item.zoneId === zoneId
      );
      const change = value - existingSelectedItem[0].quantity;

      trackItem.quantity = change < 0 ? change * -1 : change;

      itemsToAdd = selectedItems?.filter(
        (item) => item.id !== id || item.zoneId !== zoneId
      );

      // only if value bigger than 0 we update the item
      if (value > 0) {
        if (change > 0) {
          trackAddToCart(trackItem);
          trackAddToCartV4(organization, {
            eventOrMembershipId: eventOrMembership.id,
            item: trackItem,
            membership,
            release,
          });
        } else if (change < 0) {
          trackRemoveFromCart(trackItem);
          trackRemoveFromCartV4(organization, {
            eventOrMembershipId: eventOrMembership.id,
            item: trackItem,
            membership,
            release,
          });
        }

        itemsToAdd = [...itemsToAdd, updatedItem];
      } else {
        // otherwise we remove the item
        trackRemoveFromCart(trackItem);
        trackRemoveFromCartV4(organization, {
          eventOrMembershipId: eventOrMembership.id,
          item: trackItem,
          membership,
          release,
        });
      }
    }

    if (!isAdmin) {
      // Remove any selected entities that no longer meet the purchase restriction
      for (const selectedItem of itemsToAdd) {
        if (
          !isAvailableForSelection(
            itemsToAdd,
            selectedItem,
            ticketsSoldForUser
          ) ||
          selectedItem.quantity <= 0
        ) {
          const index = itemsToAdd.findIndex(
            (item) => item.id === selectedItem.id
          );

          itemsToAdd.splice(index, 1);
        }
      }

      // Display error modal if purchase restrictions fail
      if (value > 0) {
        let success = itemsToAdd.some((item) => item.id === id);

        // Check what user has previously purchased
        if (!success) {
          success = ticketsSoldForUser.some((item) =>
            [item.ticketType?.id, item.membershipType?.id].includes(id)
          );
        }

        if (!success && current?.purchaseRestrictionsHelpText) {
          const purchaseRestrictions = current.purchaseRestrictionsHelpText;

          if (purchaseRestrictions.length > 0) {
            const message = (
              <>
                <Text>{current.name} must be purchased with: </Text>
                <ul>
                  {purchaseRestrictions.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                {!user && (
                  <Text mt={4}>
                    Already purchased these?
                    <Link
                      ml={"1/2"}
                      textDecoration="underline"
                      cursor="pointer"
                      href={`/login?redirect=${encodeURIComponent(
                        `${router.asPath}?ticket=${current.id}&quantity=${value}`
                      )}`}
                    >
                      Log in
                    </Link>{" "}
                    to buy additional tickets.
                  </Text>
                )}
              </>
            );

            setAlertError({
              title: "This ticket has purchase restrictions",
              message,
            });
          }
        }
      }
    }
    setSelectedItems(itemsToAdd);
  };

  useEffect(() => {
    if (router.query.ticket && ticketTypes?.length > 0) {
      // If we have a ticket id in the query this will scroll the page to
      // the ticket listing
      const element = document.getElementById(router.query.ticket as string);
      element?.scrollIntoView({ behavior: "smooth" });
    }
  }, [router.query.ticket, ticketTypes?.length]);

  const handleOrderCreation = async () => {
    setIsSubmitting(true);
    if (seats.length === 0 && addons.length === 0) {
      showToast(
        `No ${isMembership ? "memberships" : ticketVariant} selected!`,
        "error"
      );
      return setIsSubmitting(false);
    }

    await createOrder(
      seats?.filter((seat) => {
        return seat.quantity > 0;
      }),
      addons,
      activePromotions?.[0]?.promotion?.id,
      (errorMessage) => {
        setAlertError({
          title: "There was a problem...",
          message: errorMessage,
        });

        return setIsSubmitting(false);
      }
    );
  };

  useEffect(() => {
    if (zones?.length > 0) {
      trackViewItemList(zones, ticketTypes);
      trackViewItemListV4(eventOrMembership, {
        zones,
        ticketTypes,
        seats,
        ticketsSoldForUser,
        isAdmin,
        addonTypes,
        release,
      });
    }
  }, [zones.length]);

  const calculateMaxPurchaseQuantity = (zoneTicketType) => {
    const defaultMaxPurchaseQuantity = 40;
    const remainingPassCodes =
      eventOrMembership?.digitalPassCodesStatus?.remainingPassCodes;
    const maxPurchaseQuantity = zoneTicketType?.maxPurchaseQuantity;
    if (!maxPurchaseQuantity && !remainingPassCodes) {
      return undefined;
    }

    if (!maxPurchaseQuantity && remainingPassCodes) {
      return Math.min(remainingPassCodes, defaultMaxPurchaseQuantity);
    }

    if (maxPurchaseQuantity && !remainingPassCodes) {
      return maxPurchaseQuantity;
    }

    return Math.min(remainingPassCodes, maxPurchaseQuantity);
  };

  const totalOptions = {
    showHiddenFees,
    fee: eventOrMembership?.bookingFee,
    type: eventOrMembership?.bookingFeeType,
  };

  const subTotal = calculateTotal(
    [...seats, ...addons],
    [...addonTypes, ...ticketTypes],
    zones,
    totalOptions
  );

  const referralDiscount = calculateReferralDiscount(
    activeReferralCampaign,
    calculateTotal(
      [...seats, ...addons],
      [...addonTypes, ...ticketTypes],
      zones
    ) - multibuyDiscount
  );

  const totalPrice = subTotal - multibuyDiscount - referralDiscount;

  const hasTicketSelected = seats?.length > 0 || addons?.length > 0;

  const showIsR18 = "isR18" in eventOrMembership && eventOrMembership.isR18;

  // this value has derived from the zone ticketTypes which uses a ResolvedField see backend for more info.
  const hasSoldOut = zones?.some((z) =>
    z?.ticketTypes
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .filter((tt) => tt.isActive)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .every((tt) => tt.isSoldOut ?? false)
  );

  const isEventAndHasResaleWaitlist =
    (eventOrMembership.__typename !== "Membership" &&
      eventOrMembership.ticketResaleWaitlistEnabled) ??
    false;

  const actionButton = () => {
    let actionButtonString = "";
    actionButtonString = actionButtonString.concat(
      actionButtonString,
      calculateTotal(
        [...seats, ...addons],
        [...addonTypes, ...ticketTypes],
        zones
      ) -
        multibuyDiscount ===
        0
        ? "Get"
        : "Buy",
      " ",
      organization.landingPage === LandingPage.SingleEvent
        ? "ticket"
        : isMembership
        ? "membership"
        : ticketVariant,
      "s"
    );

    return actionButtonString;
  };

  if (delayedIsSubmitting) {
    return (
      <Box maxW={{ _: "100%", md: "640px" }} m={{ _: 0, md: "0 auto" as any }}>
        <Box my={20}>
          <Box mx={"1/2"} my={20} px={4}>
            <Spinner size={64} />
            <Text
              mt={4}
              fontSize={4}
              lineHeight="high"
              color="N600"
              textAlign="center"
            >
              We&apos;re just double-checking that you&apos;re not a
              ticket-buying robot in disguise.
            </Text>
            <Text
              mt={1}
              fontSize={4}
              lineHeight="high"
              color="N600"
              textAlign="center"
            >
              {" "}
              Your tickets are nearly ready.
            </Text>
            <Text
              textAlign="center"
              mt={4}
              fontSize={4}
              lineHeight="high"
              color="N600"
              fontWeight={"heavy"}
            >
              Don&apos;t refresh this page.
            </Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box maxW={{ _: "100%", md: "640px" }} m={{ _: 0, md: "0 auto" as any }}>
      {hasImportantNotice(importantNotice) && !isPOS && (
        <Box
          w="100%"
          p={2}
          bg="N100"
          borderRadius="sm"
          border="1px"
          borderColor="N300"
          mb={showIsR18 ? 2 : 5}
        >
          <Flex mb={3}>
            <Icon icon="info" color="N600" fontSize={6} />
            <Text
              fontSize={6}
              lineHeight="normal"
              fontWeight="heavy"
              color="N800"
              ml={1}
            >
              Important notes
            </Text>
          </Flex>
          <SlateRenderer value={importantNotice} />
        </Box>
      )}
      {showIsR18 && (
        <Flex
          alignItems="center"
          borderRadius="sm"
          backgroundColor="error25"
          px="6/4"
          py={2}
          mb={5}
        >
          <Icon icon="r18" fontSize={6} mr={1} />
          <Text
            fontWeight="medium"
            fontSize={2}
            lineHeight="medium"
            color="N700"
          >
            This is an R18 event
          </Text>
        </Flex>
      )}
      <Text
        color="N800"
        fontWeight="heavy"
        fontSize={{ _: 6, md: 7 }}
        textAlign={{ _: "center", md: "center" }}
        lineHeight={{ _: "normal", md: "high" }}
        mb={2}
      >
        Select{" "}
        {isSinglePage
          ? `${startCase(ticketVariant)}s`
          : isMembership
          ? "Memberships"
          : `${startCase(ticketVariant)}s`}
      </Text>

      {zones?.map((zone) => {
        if (!zone.isActive) {
          return null;
        }

        const ticketListItems = ticketTypes
          ?.sort((a, b) => (a?.createdAt > b?.createdAt ? 1 : -1))
          ?.map((ticketType) => {
            const zoneTypes = zone?.ticketTypes ?? zone?.membershipTypes;
            const zoneTicketType = zoneTypes?.find(
              ({ ticketTypeId, membershipTypeId }) =>
                ticketTypeId === ticketType.id ||
                membershipTypeId === ticketType.id
            );

            if (
              !zoneTicketType ||
              (!zoneTicketType.isActive && releaseType !== ReleaseType.Presale)
            ) {
              return false;
            }

            if (!isVisible(seats, ticketType, ticketsSoldForUser) && !isAdmin) {
              return null;
            }

            const selectedQuantity = seats?.find(
              (seat) => seat.id === ticketType.id && seat.zoneId === zone?.name
            );

            return (
              <ListItem
                key={`${zone?.name}-${ticketType.id}`}
                ticketType={{
                  ...ticketType,
                  ...allTypes?.find(
                    (type) =>
                      type?.ticketTypeId === ticketType.id ||
                      type?.membershipTypeId === ticketType.id
                  ),
                  name: `${ticketType.name}`,
                  price: zoneTicketType.price,
                  zoneId: zone?.name,
                }}
                showHiddenFees={showHiddenFees}
                handleChange={(
                  id: string,
                  quantity: number,
                  type: "event" | "membership" | "addon"
                ) =>
                  onChange(id, quantity, type, zone?.name, zoneTicketType.price)
                }
                quantity={selectedQuantity?.quantity ?? 0}
                isAdmin={isAdmin || isPOS}
                ticketAvailabilityStatus={
                  zoneTicketType?.ticketAvailabilityStatus
                }
                minPurchaseQuantity={
                  isMembership && "minPurchaseQuantity" in ticketType
                    ? ticketType.minPurchaseQuantity
                    : zoneTicketType?.minPurchaseQuantity
                }
                maxPurchaseQuantity={
                  isMembership && "maxPurchaseQuantity" in ticketType
                    ? ticketType.maxPurchaseQuantity
                    : calculateMaxPurchaseQuantity(zoneTicketType)
                }
              />
            );
          })
          .filter((tt) => tt !== false);

        // Only show zone heading if there are ticket types available for purchase
        if (ticketListItems.length > 0) {
          const hasZoneDescription =
            zone?.description && zone.description.trim();

          const items = ticketListItems.filter((tt) => tt);

          if (!hasZoneDescription && items.length === 0) {
            return null;
          }

          return (
            <Box mb={4} key={zone?.name}>
              <Text
                color="N800"
                fontWeight="heavy"
                fontSize={{ _: 6, md: 7 }}
                textAlign={{ _: "left", md: "left" }}
                lineHeight="normal"
              >
                {zone?.name}
              </Text>
              {hasZoneDescription && (
                <Box
                  w="100%"
                  p={2}
                  bg="N100"
                  borderRadius="sm"
                  border="1px"
                  borderColor="N300"
                  mt={2}
                >
                  <Text
                    color="N800"
                    fontSize={3}
                    textAlign={{ _: "left", md: "left" }}
                    lineHeight="medium"
                  >
                    {zone?.description}
                  </Text>
                </Box>
              )}
              {items.length > 0 && (
                <>
                  <Divider my={{ _: 2, md: 2 }} />
                  {items}
                </>
              )}
            </Box>
          );
        }
      })}

      {addonTypes.length > 0 && (
        <>
          <Text
            color="N800"
            fontWeight="heavy"
            fontSize={{ _: 6, md: 7 }}
            textAlign={{ _: "center", md: "center" }}
            lineHeight={{ _: "normal", md: "high" }}
            mt={4}
            mb={1}
          >
            Select Addons
          </Text>
          <Divider mt={{ _: 2, md: 0 }} mb={{ _: 2, md: 3 }} />

          {addonTypes.map((addonType) => {
            const selectedQuantity = addons?.find(
              (addon) => addon.id === addonType.id && addon.zoneId === "no-zone"
            );

            return (
              <ListItem
                key={addonType.id}
                addonType={addonType}
                handleChange={(
                  id: string,
                  quantity: number,
                  type: "event" | "membership" | "addon"
                ) => onChange(id, quantity, type, "no-zone", addonType.price)}
                quantity={selectedQuantity?.quantity ?? 0}
                isAdmin={isAdmin || isPOS}
              />
            );
          })}
        </>
      )}
      <Divider mt={{ _: 3, md: 0 }} />
      {multibuyDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Sub-Total
            </Text>
            <Price
              price={calculateTotal(
                [...seats, ...addons],
                [...addonTypes, ...ticketTypes],
                zones
              )}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            />
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Multibuy promotion applied
            </Text>
            <Price
              price={-multibuyDiscount}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            />
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {activeReferralCampaign &&
        activeReferralCampaign.referralUserRewardType ===
          ReferralRewardType.Discount && (
          <>
            <Flex mt={2} justifyContent="space-between" w="100%">
              <Text fontWeight="medium" fontSize={2} color="N600">
                Sub-Total
              </Text>
              <Price
                price={calculateTotal(
                  [...seats, ...addons],
                  [...addonTypes, ...ticketTypes],
                  zones
                )}
                fontWeight="medium"
                fontSize={4}
                lineHeight={"18px" as any}
                color={"black" as any}
              />
            </Flex>
            <Flex mt={2} justifyContent="space-between" w="100%">
              <Text fontWeight="medium" fontSize={2} color="N600">
                Referral discount applied
              </Text>
              <Price
                price={
                  -calculateReferralDiscount(
                    activeReferralCampaign,
                    calculateTotal(
                      [...seats, ...addons],
                      [...addonTypes, ...ticketTypes],
                      zones
                    ) - multibuyDiscount
                  )
                }
                fontWeight="medium"
                fontSize={4}
                lineHeight={"18px" as any}
                color={"black" as any}
              />
            </Flex>
            <Divider my={{ _: 3, md: 2 }} />
          </>
        )}

      <Flex mt={2} justifyContent="space-between" w="100%">
        <Text fontWeight="medium" fontSize={2} color="N600">
          Total {showHiddenFees ? "(including booking fees)" : ""}
        </Text>
        <Text
          fontWeight="medium"
          fontSize={4}
          lineHeight={"18px" as any}
          color={"black" as any}
        >
          {hasTicketSelected ? <Price price={totalPrice} /> : "-"}
        </Text>
      </Flex>

      {userCredits > 0 && hasTicketSelected && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Your credits to be deducted
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {`- $${user.credits.toFixed(2)}`}
            </Text>
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total left to pay after deducting credits
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {totalPrice - userCredits > 0 ? (
                <Price price={totalPrice - userCredits} />
              ) : (
                "$0.00"
              )}
            </Text>
          </Flex>
        </>
      )}

      {!isPOS && totalPrice - userCredits > 0 && (
        <>
          {bnplGateways.length > 0 &&
            bnplGateways.map((g) => (
              <Box mt={2} w="100%" key={g.id}>
                <BuyNowPayLater
                  totalPrice={totalPrice - userCredits}
                  hasTicketSelected={hasTicketSelected}
                  showHiddenFees={showHiddenFees}
                  isSmallDisplay={isMobile}
                  bnplIntegration={g as BNPLIntegration} // This was checked when filtering.
                />
              </Box>
            ))}
          {stripeAfterpayGateway && (
            <Flex flexDirection={"row-reverse"}>
              {/* @ts-expect-error - Afterpay placement component */}
              <afterpay-placement
                data-locale={getAfterPayLocaleByCurrency(organization.currency)}
                data-currency={organization.currency ?? "NZD"}
                data-amount={totalPrice - userCredits}
              >
                {/* @ts-expect-error - Afterpay placement component */}
              </afterpay-placement>
            </Flex>
          )}
        </>
      )}

      <PrimaryButton
        mt={{ _: 2, md: 6 }}
        padding="mobileGutter"
        w="100%"
        onClick={handleOrderCreation}
        isLoading={isSubmitting}
        disabled={hasTicketSelected === false}
      >
        {organization.id === "2648ddb4-b50c-489f-a3eb-ae0249e3db3c"
          ? "Join now"
          : actionButton()}
      </PrimaryButton>
      {hasSoldOut &&
        hasFeature("ticketResale") &&
        isEventAndHasResaleWaitlist &&
        !isResaleRelease && (
          <Flex
            pt={4}
            flexDirection={"column"}
            textAlign={"center"}
            justifyContent={"center"}
          >
            <Text
              paddingBottom={2}
              fontWeight={"demiBold"}
              fontSize={6}
              color="N600"
            >
              Are the tickets you wanted sold out?
            </Text>
            <Text paddingBottom={4}>
              Join the waitlist and we will let you know if more tickets become
              available!
            </Text>
            <SecondaryButton
              to={`/events/${eventOrMembership.id}/ticket-waitlist`}
              alignSelf={"center"}
              width="fit-content"
            >
              Join waitlist
            </SecondaryButton>
          </Flex>
        )}
      {/* DON"T REMOVE: 
       When youre on safari the buy tickets button is hidden as the nav bar is at the bottom. 
       We might be able to add bottom padding or use dvh css property */}
      <br />
      <br />
      <br />
      <br />
      <br />

      <ContentModal
        isOpen={!!alertError}
        setIsOpen={() => setAlertError(undefined)}
        title={alertError?.title}
      >
        {typeof alertError?.message === "string" ? (
          <div dangerouslySetInnerHTML={{ __html: alertError?.message }} />
        ) : (
          alertError?.message
        )}
      </ContentModal>
    </Box>
  );
};
